var __jsx = React.createElement;
import "core-js/modules/es7.string.pad-start.js";
import "core-js/modules/es6.string.ends-with.js";
import React, { useState, useEffect, useRef } from 'react';
import { CloseOutlined, RightCircleFilled } from '@ant-design/icons';
import { useSelector } from 'react-redux';

// Countdown to the Evercode MAX marketing launch: 20 Oct 2026, 12:00 UTC (JS months are 0-indexed)
var EVERCODE_MAX_LAUNCH_TIME = Date.UTC(2026, 9, 20, 12, 0, 0);
var PRE_LAUNCH_URL = 'https://www.parsebiosciences.com/what-would-you-maximize/?utm_campaign=2026-launch-evercode-max&utm_source=Trailmaker';
var LAUNCH_URL = 'https://www.parsebiosciences.com/products/evercode-max-wt/';
var DISMISSED_KEY = 'evercodeMaxBannerDismissed';
var getRemaining = function getRemaining() {
  return Math.max(EVERCODE_MAX_LAUNCH_TIME - Date.now(), 0);
};
var pad = function pad(value) {
  return String(value).padStart(2, '0');
};
var CountdownTile = function CountdownTile(_ref) {
  var value = _ref.value,
    label = _ref.label;
  return __jsx("span", {
    style: {
      display: 'inline-flex',
      alignItems: 'baseline',
      background: 'rgba(255,255,255,0.15)',
      borderRadius: 4,
      padding: '3px 6px',
      lineHeight: 1,
      fontVariantNumeric: 'tabular-nums'
    }
  }, __jsx("span", {
    style: {
      fontSize: '0.9rem',
      fontWeight: 700
    }
  }, pad(value)), __jsx("span", {
    style: {
      fontSize: '0.65rem',
      color: 'rgba(255,255,255,0.7)',
      marginLeft: 2
    }
  }, label));
};
var useRemaining = function useRemaining() {
  var _useState = useState(getRemaining),
    remaining = _useState[0],
    setRemaining = _useState[1];
  useEffect(function () {
    if (remaining <= 0) return undefined;
    var interval = setInterval(function () {
      return setRemaining(getRemaining());
    }, 1000);
    return function () {
      return clearInterval(interval);
    };
  }, [remaining <= 0]);
  return remaining;
};
var Countdown = function Countdown(_ref2) {
  var remaining = _ref2.remaining;
  return __jsx("span", {
    style: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6
    }
  }, __jsx(CountdownTile, {
    value: Math.floor(remaining / 86400000),
    label: "d"
  }), __jsx(CountdownTile, {
    value: Math.floor(remaining % 86400000 / 3600000),
    label: "h"
  }), __jsx(CountdownTile, {
    value: Math.floor(remaining % 3600000 / 60000),
    label: "m"
  }), __jsx(CountdownTile, {
    value: Math.floor(remaining % 60000 / 1000),
    label: "s"
  }));
};
var TopBanner = function TopBanner(_ref3) {
  var onHeightChange = _ref3.onHeightChange;
  var email = useSelector(function (state) {
    return state.user.current.attributes.email;
  });
  var _useState2 = useState(false),
    visible = _useState2[0],
    setVisible = _useState2[1];
  var bannerRef = useRef(null);
  var remaining = useRemaining();
  var isLaunched = remaining <= 0;
  useEffect(function () {
    var dismissed = sessionStorage.getItem(DISMISSED_KEY);
    if (!dismissed && !email.endsWith('@parsebiosciences.com')) {
      setVisible(true);
    }
  }, []);
  var handleDismiss = function handleDismiss() {
    setVisible(false);
    sessionStorage.setItem(DISMISSED_KEY, 'true');
  };
  useEffect(function () {
    if (!visible) {
      onHeightChange(0);
      return;
    }
    var updateHeight = function updateHeight() {
      var _bannerRef$current$of, _bannerRef$current;
      var bannerHeight = (_bannerRef$current$of = (_bannerRef$current = bannerRef.current) === null || _bannerRef$current === void 0 ? void 0 : _bannerRef$current.offsetHeight) !== null && _bannerRef$current$of !== void 0 ? _bannerRef$current$of : 0;
      onHeightChange(bannerHeight);
    };
    updateHeight();
    var resizeObserver;
    if (typeof ResizeObserver !== 'undefined' && bannerRef.current) {
      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(bannerRef.current);
    }
    return function () {
      var _resizeObserver;
      (_resizeObserver = resizeObserver) === null || _resizeObserver === void 0 || _resizeObserver.disconnect();
      onHeightChange(0);
    };
  }, [visible, onHeightChange]);
  if (!visible) return null;
  return __jsx("div", {
    ref: bannerRef,
    style: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      zIndex: 1000,
      background: '#B6007C',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      padding: '10px 48px',
      fontWeight: 500,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }
  }, !isLaunched && __jsx(Countdown, {
    remaining: remaining
  }), __jsx("a", {
    href: isLaunched ? LAUNCH_URL : PRE_LAUNCH_URL,
    target: "_blank",
    rel: "noreferrer",
    style: {
      textDecoration: 'none'
    }
  }, __jsx("span", {
    style: {
      color: 'white',
      fontWeight: 400
    }
  }, isLaunched ? 'Introducing Evercode MAX. Explore Now' : 'Something New is Coming to Evercode', ' ', __jsx(RightCircleFilled, null))), __jsx("span", {
    style: {
      position: 'absolute',
      right: 16,
      top: '50%',
      transform: 'translateY(-50%)',
      cursor: 'pointer'
    },
    onClick: handleDismiss,
    onKeyDown: function onKeyDown(e) {
      if (e.key === 'Enter' || e.key === ' ') handleDismiss();
    },
    "aria-label": "Close banner",
    role: "button",
    tabIndex: 0
  }, __jsx(CloseOutlined, {
    style: {
      fontSize: '18px',
      color: 'white'
    }
  })));
};
TopBanner.defaultProps = {
  onHeightChange: function onHeightChange() {}
};
export default TopBanner;